import { ListingOfferCardLarge } from 'app/components/listing-offer-card.large';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import type { ClosestListingsByTag } from 'app/network/listing';
import type { RecentLoanListing } from 'app/network/loan';
import { HorizontalListingsSkeleton } from 'app/screens/billboard/all-listings/components/horizontal-listings-skeleton';
import {
  getRecommendationTitleKey,
  Strategies,
  type RecommendationSectionState,
  RecommendationQuery,
  getRecommendationItemKeys,
  type RecommendedListingsComponentProps,
} from 'app/screens/billboard/all-listings/components/recommended-listings';
import { createTheme as createDSTheme } from 'app/shopify';
import type { Listing } from 'app/types/listing';
import type { RecommendedListing } from 'app/types/recommender';
import type { SharingPlace } from 'app/types/sharing-place';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react-native';
import React, { useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FeatureFlag, isFeatureDisabled } from 'app/utils/helpers';

const theme = createDSTheme();
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

type DesktopRecommendation = Listing | RecommendedListing | RecentLoanListing | SharingPlace;
type StrategyProps = RecommendedListingsComponentProps;

interface DesktopRecommendationSectionProps<T extends DesktopRecommendation = DesktopRecommendation> {
  strategyProps: StrategyProps;
  recommendationState?: RecommendationSectionState;
  recommendations?: T[];
  onViewAll?: () => void;
  titleSuffix?: React.ReactNode;
  renderItem?: (listing: T, isNew: boolean) => React.ReactNode;
  emptyState?: React.ReactNode;
  extraPadding?: number;
}

export function DesktopRecommendationSection<T extends DesktopRecommendation = DesktopRecommendation>(
  props: DesktopRecommendationSectionProps<T>,
) {
  const { recommendationState } = props;
  return recommendationState ? (
    <DesktopRecommendationSectionContent {...props} recommendationState={recommendationState} />
  ) : (
    <DesktopRecommendationSectionWithQuery {...props} />
  );
}

function DesktopRecommendationSectionWithQuery<T extends DesktopRecommendation>(
  props: DesktopRecommendationSectionProps<T>,
) {
  return (
    <RecommendationQuery strategyProps={props.strategyProps}>
      {(recommendationState) => (
        <DesktopRecommendationSectionContent {...props} recommendationState={recommendationState} />
      )}
    </RecommendationQuery>
  );
}

function getDesktopRecommendations<T extends DesktopRecommendation>(
  recommendations: RecommendationSectionState['recommendations'] | T[] | undefined,
) {
  if (!recommendations?.length) return undefined;
  if ('tagId' in (recommendations[0] ?? {})) {
    return (recommendations as ClosestListingsByTag[]).flatMap((result) => result.listings);
  }
  return recommendations as T[];
}

function areDesktopRecommendationsDisabled(strategy: Strategies) {
  if (!isFeatureDisabled(FeatureFlag.showRecommendedForYou)) return false;
  return ![
    Strategies.NeighbourOffers,
    Strategies.NeighbourRequests,
    Strategies.NearestSharingPlace,
    Strategies.ByCategory,
  ].includes(strategy);
}

interface DesktopRecommendationHeaderProps {
  canScroll: boolean;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  horizontalPadding: number;
  onNextButtonPressed: (type: 'next' | 'previous') => void;
  onViewAll?: () => void;
  title: string;
  titleSuffix?: React.ReactNode;
}

function DesktopRecommendationHeader({
  canScroll,
  canScrollLeft,
  canScrollRight,
  horizontalPadding,
  onNextButtonPressed,
  onViewAll,
  title,
  titleSuffix,
}: DesktopRecommendationHeaderProps) {
  const { t } = useTranslation('recommendations');
  return (
    <Row justifyContent="space-between" alignItems="center" style={{ paddingHorizontal: horizontalPadding }}>
      <Pressable onPress={onViewAll} disabled={!onViewAll}>
        <Row alignItems="center" gap="spacing.general.x1">
          <Text fontSize={20} fontWeight="700" variant="bodyBase">
            {title}
          </Text>
          {typeof titleSuffix === 'string' ? null : titleSuffix}
          {onViewAll ? (
            <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={20} color={theme.colors['text.primary']} />
          ) : null}
        </Row>
      </Pressable>

      <Row alignItems="center" gap="spacing.general.x4">
        {onViewAll ? (
          <Pressable onPress={onViewAll}>
            <Text color="text.interactive" fontSize={14} fontWeight="600">
              {t('landing-page:viewMore')}
            </Text>
          </Pressable>
        ) : null}
        {canScroll ? (
          <DesktopCarouselButtons
            canScrollLeft={canScrollLeft}
            canScrollRight={canScrollRight}
            onPress={onNextButtonPressed}
          />
        ) : null}
      </Row>
    </Row>
  );
}

interface DesktopRecommendationBodyProps<T extends DesktopRecommendation> {
  emptyState?: React.ReactNode;
  extraPadding: number;
  horizontalPadding: number;
  listings: T[];
  mountedAt: number;
  onContainerWidthChange: (width: number) => void;
  onContentWidthChange: (width: number) => void;
  onScroll: NonNullable<React.ComponentProps<typeof ScrollView>['onScroll']>;
  onScrollEnd: NonNullable<React.ComponentProps<typeof ScrollView>['onScroll']>;
  renderItem?: (listing: T, isNew: boolean) => React.ReactNode;
  scrollRef: React.RefObject<ScrollView | null>;
  shouldShowListings: boolean;
  strategy: Strategies;
}

function DesktopRecommendationBody<T extends DesktopRecommendation>({
  emptyState,
  extraPadding,
  horizontalPadding,
  listings,
  mountedAt,
  onContainerWidthChange,
  onContentWidthChange,
  onScroll,
  onScrollEnd,
  renderItem,
  scrollRef,
  shouldShowListings,
  strategy,
}: DesktopRecommendationBodyProps<T>) {
  if (!shouldShowListings) return emptyState ? <Column style={styles.emptyState}>{emptyState}</Column> : null;
  const itemKeys = getRecommendationItemKeys(listings, strategy);

  return (
    <ScrollView
      ref={scrollRef}
      horizontal
      onLayout={(event) => {
        onContainerWidthChange(event.nativeEvent.layout.width);
        scrollRef.current?.scrollTo({ x: 0, animated: false });
      }}
      onContentSizeChange={onContentWidthChange}
      onMomentumScrollEnd={onScrollEnd}
      onScroll={onScroll}
      onScrollEndDrag={onScrollEnd}
      showsHorizontalScrollIndicator={false}
      style={styles.horizontalList}
    >
      <Row
        gap="spacing.general.x2"
        style={[styles.horizontalListContent, { paddingHorizontal: horizontalPadding + extraPadding }]}
      >
        {listings.map((listing, index) => {
          const createdAt = (listing as { createdAt?: string }).createdAt;
          const isNew =
            strategy !== Strategies.RecentOffers &&
            (createdAt ? new Date(createdAt).getTime() > mountedAt - ONE_WEEK_MS : false);
          return (
            <View key={itemKeys[index]} style={styles.listingCardSlot}>
              {renderItem ? (
                renderItem(listing, isNew)
              ) : (
                <ListingOfferCardLarge listing={listing as unknown as Listing} isNew={isNew} />
              )}
            </View>
          );
        })}
      </Row>
    </ScrollView>
  );
}

function DesktopRecommendationSectionContent<T extends DesktopRecommendation>({
  strategyProps,
  recommendationState,
  recommendations: recommendationsProp,
  onViewAll,
  titleSuffix,
  renderItem,
  emptyState,
  extraPadding = 0,
}: DesktopRecommendationSectionProps<T> & { recommendationState: RecommendationSectionState }) {
  const { t } = useTranslation('recommendations');
  const scrollRef = useRef<ScrollView>(null);
  const currentOffsetXRef = useRef(0);
  const [currentOffsetX, setCurrentOffsetX] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [mountedAt] = useReducer(
    (timestamp: number) => timestamp,
    0,
    () => Date.now(),
  );
  const insets = useSafeAreaInsets();
  const horizontalPadding = insets.left || theme.spacing['spacing.general.x4'];

  const recommendations = recommendationsProp ?? recommendationState.recommendations;
  const listingsRaw = getDesktopRecommendations<T>(recommendations);
  const { isPending, show } = recommendationState;
  useRegisterLoading(isPending, `LandingPageScreen-${strategyProps.strategy}`);
  const listings = listingsRaw ?? [];
  const recommendationsFeatureDisabled = areDesktopRecommendationsDisabled(strategyProps.strategy);

  const maxOffset = Math.max(0, contentWidth - containerWidth);
  const canScroll = maxOffset > 0;
  const canScrollRight = canScroll && currentOffsetX < maxOffset;
  const canScrollLeft = canScroll && currentOffsetX > 0;

  const onNextButtonPressed = (type: 'next' | 'previous') => {
    if (!canScroll) return;
    const rawOffset = type === 'next' ? currentOffsetXRef.current + 728 : currentOffsetXRef.current - 728;
    const nextOffset = Math.min(Math.max(0, rawOffset), maxOffset);
    scrollRef.current?.scrollTo({ animated: true, x: nextOffset });
    currentOffsetXRef.current = nextOffset;
    setCurrentOffsetX(nextOffset);
  };

  const commitScrollOffset = (
    event: Parameters<NonNullable<React.ComponentProps<typeof ScrollView>['onScroll']>>[0],
  ) => {
    const nextOffset = Math.min(Math.max(0, event.nativeEvent.contentOffset.x), maxOffset);
    currentOffsetXRef.current = nextOffset;
    setCurrentOffsetX(nextOffset);
  };

  const handleContainerLayout = (nextContainerWidth: number) => {
    setContainerWidth(nextContainerWidth);
    currentOffsetXRef.current = 0;
    setCurrentOffsetX(0);
  };

  const handleContentSizeChange = (nextContentWidth: number) => {
    setContentWidth(nextContentWidth);
    const nextOffset = Math.min(currentOffsetXRef.current, Math.max(0, nextContentWidth - containerWidth));
    currentOffsetXRef.current = nextOffset;
    setCurrentOffsetX(nextOffset);
  };

  const trackScrollPosition: NonNullable<React.ComponentProps<typeof ScrollView>['onScroll']> = (event) => {
    currentOffsetXRef.current = event.nativeEvent.contentOffset.x;
  };

  const title = t(getRecommendationTitleKey(strategyProps), {
    tagName: typeof titleSuffix === 'string' ? titleSuffix : undefined,
    categoryName: typeof titleSuffix === 'string' ? titleSuffix : undefined,
  });

  if (recommendationsFeatureDisabled) return null;
  if (isPending) return <HorizontalListingsSkeleton />;
  if (!listings.length && !emptyState) return null;

  const shouldShowListings = Boolean((recommendationsProp || show) && listings.length);
  if (!shouldShowListings && !emptyState) return null;

  return (
    <Column gap="spacing.general.x2" style={styles.recommendationSection}>
      <DesktopRecommendationHeader
        canScroll={canScroll}
        canScrollLeft={canScrollLeft}
        canScrollRight={canScrollRight}
        horizontalPadding={horizontalPadding}
        onNextButtonPressed={onNextButtonPressed}
        onViewAll={onViewAll}
        title={title}
        titleSuffix={titleSuffix}
      />
      <DesktopRecommendationBody
        emptyState={emptyState}
        extraPadding={extraPadding}
        horizontalPadding={horizontalPadding}
        listings={listings as T[]}
        mountedAt={mountedAt}
        onContainerWidthChange={handleContainerLayout}
        onContentWidthChange={handleContentSizeChange}
        onScroll={trackScrollPosition}
        onScrollEnd={commitScrollOffset}
        renderItem={renderItem}
        scrollRef={scrollRef}
        shouldShowListings={shouldShowListings}
        strategy={strategyProps.strategy}
      />
    </Column>
  );
}

function DesktopCarouselButtons({
  canScrollLeft,
  canScrollRight,
  onPress,
}: {
  canScrollLeft: boolean;
  canScrollRight: boolean;
  onPress: (type: 'next' | 'previous') => void;
}) {
  const { t } = useTranslation('landing-page');
  const controlButtonSize = Number(theme.tokens.sizing['sizing.general.x8']);
  return (
    <Row gap="spacing.general.x2">
      <Button
        width={controlButtonSize}
        height={controlButtonSize}
        minHeight={0}
        variant="primary"
        justifyContent="center"
        alignItems="center"
        style={canScrollLeft ? styles.carouselButton : styles.carouselButtonMuted}
        onPress={() => onPress('previous')}
        accessibilityLabel={t('carousel.previousSlide', { ns: 'accessibility' })}
        accessibilityHint={t('carousel.previousSlideHint', { ns: 'accessibility' })}
        disabled={!canScrollLeft}
      >
        <IconChevronLeft
          strokeWidth={ICON_STROKE_WIDTH}
          size={24}
          color={!canScrollLeft ? theme.colors['text.primary'] : theme.colors['surface.inverse.tertiary-neutral']}
        />
      </Button>
      <Button
        width={controlButtonSize}
        height={controlButtonSize}
        minHeight={0}
        variant="primary"
        justifyContent="center"
        alignItems="center"
        style={canScrollRight ? styles.carouselButton : styles.carouselButtonMuted}
        onPress={() => onPress('next')}
        accessibilityLabel={t('carousel.nextSlide', { ns: 'accessibility' })}
        accessibilityHint={t('carousel.nextSlideHint', { ns: 'accessibility' })}
        disabled={!canScrollRight}
      >
        <IconChevronRight
          strokeWidth={ICON_STROKE_WIDTH}
          size={24}
          color={!canScrollRight ? theme.colors['text.primary'] : theme.colors['surface.inverse.tertiary-neutral']}
        />
      </Button>
    </Row>
  );
}

const styles = StyleSheet.create({
  recommendationSection: { width: '100%' },
  horizontalList: { width: '100%' },
  horizontalListContent: { paddingHorizontal: theme.spacing['spacing.general.x2'] },
  listingCardSlot: { flexShrink: 0 },
  emptyState: { borderRadius: theme.borderRadii['radius.lg'] },
  carouselButtonMuted: {
    backgroundColor: theme.colors['surface.tertiary-neutral'],
    borderRadius: 22,
  },
  carouselButton: {
    backgroundColor: theme.colors['text.primary'],
    borderRadius: 22,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
  },
});
